import { Dialog } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { Radio } from '../../../../../../widgets/radio';
import { Select } from '../../../../../../widgets/select';
import React from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import ComposerUploadComponent from '../components/composerUploadComponent';

export enum ComposerScreenshotType {
    Object,
    Material,
    Upload
}
const typeSelector = <T,>(type: ComposerScreenshotType, choice1: T, choice2: T, choice3: T): T => {
    return type === ComposerScreenshotType.Object ? choice1 : type === ComposerScreenshotType.Material ? choice2 : choice3;
};
interface ComposerScreenshotOutput {
    type: ComposerScreenshotType;
    attributeFilter: string;
    optionsFilter: string;
    material?: string;
    upload?: string;
}
interface ComposerScreenshotAttributeDialogProps {
    materialsList: BABYLON.Material[];
    onConfirm: (attributes: ComposerScreenshotOutput) => void;
}
const ComposerSaveAttributeDialogWindow = styled(Dialog)`
    max-width: 600px;
`;
const Title = styled.h3`
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
`;

const typeOptions = [
    { value: ComposerScreenshotType.Object, label: T._('Object', 'Admin') },
    { value: ComposerScreenshotType.Material, label: T._('Material', 'Admin') },
    { value: ComposerScreenshotType.Upload, label: T._('Upload', 'Admin') }
];

const ComposerScreenshotAttributeDialog: React.FC<ComposerScreenshotAttributeDialogProps> = ({ materialsList, onConfirm }) => {
    const [type, setType] = React.useState<ComposerScreenshotType>(ComposerScreenshotType.Object);
    const [attributeFilter, setAttributeFilter] = React.useState<string>('');
    const [optionsFilter, setOptionsFilter] = React.useState<string>('');

    const [material, setMaterial] = React.useState<string | undefined>(undefined);
    const [upload, setUpload] = React.useState<string | undefined>(undefined);

    const handleConfirm = () => {
        const screenshot: ComposerScreenshotOutput = {
            type,
            attributeFilter,
            optionsFilter,
            material,
            upload
        };
        onConfirm(screenshot);
    };

    const form = React.useMemo(() => {
        const baseForm = (
            <>
                <FormControl
                    label={T._('Attributes filter (optional)', 'Admin')}
                    tooltipLabelHtml={T._('The attributes filter excludes only attributes that match the filter', 'Admin')}
                >
                    <Input
                        placeholder={T._('You can use wildcards like *', 'Admin')}
                        type='text'
                        value={attributeFilter}
                        onChange={(e) => setAttributeFilter(e.target.value)}
                    />
                </FormControl>
                <FormControl
                    label={T._('Options filter (optional)', 'Admin')}
                    tooltipLabelHtml={T._('The name of the attribute that will be displayed in the configurator', 'Admin')}
                >
                    <Input
                        placeholder={T._('You can use wildcards like *', 'Admin')}
                        type='text'
                        value={optionsFilter}
                        onChange={(e) => setOptionsFilter(e.target.value)}
                    />
                </FormControl>
            </>
        );
        return [
            <FormControl label={T._('Sceenshot type', 'Admin')} tooltipLabelHtml={T._('Select the type of screenshot to apply', 'Admin')}>
                {typeOptions.map((option) => (
                    <Radio
                        key={option.value}
                        name='screenshotType'
                        label={option.label}
                        value={option.value}
                        checked={type === option.value}
                        onSelect={() => setType(option.value)}
                    />
                ))}
            </FormControl>,
            typeSelector<React.ReactNode>(
                type,
                <></>,
                <>
                    <FormControl label={T._('Material', 'Admin')} tooltipLabelHtml={T._('Screenshot will be fetched from the material', 'Admin')}>
                        <Select value={material} onChange={(e) => setMaterial(e)}>
                            {materialsList.map(({ id, name }) => (
                                <option key={id} value={id}>
                                    {name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                </>,
                <>
                    <FormControl label={T._('Upload', 'Admin')} tooltipLabelHtml={T._('Screenshot will be uploaded from your computer', 'Admin')}>
                        <ComposerUploadComponent
                            onImagePick={(url) => {
                                console.log('Image uploaded:', url);
                                setUpload(url);
                            }}
                        />
                    </FormControl>
                </>
            ),
            baseForm
        ];
    }, [type]);

    return (
        <Dialog windowDecorator={ComposerSaveAttributeDialogWindow} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <Title>{T._('Apply Screenshot to Multiple Options', 'Admin')}</Title>
            <Container>{form}</Container>
        </Dialog>
    );
};

export default ComposerScreenshotAttributeDialog;
